import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import PageHero from "@/components/base/PageHero";
import FeedbackForm from "@/pages/about/components/FeedbackForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const scopes = [
  {
    icon: "ri-newspaper-line",
    title: "动漫资讯",
    desc: "汇总 TV 动画、剧场版与漫画新闻，按时间与热度整理，第一时间掌握行业动态。",
  },
  {
    icon: "ri-movie-line",
    title: "作品推荐",
    desc: "按类型、题材、地区与年份筛选热门与经典作品，帮助你发现下一部想追的番。",
  },
  {
    icon: "ri-book-open-line",
    title: "内容解读",
    desc: "围绕作品背景、设定与看点提供深入解读，让理解作品这件事变得更有趣。",
  },
  {
    icon: "ri-bookmark-3-line",
    title: "专题合集",
    desc: "以主题聚合内容，把分散的资讯与作品整理成一条清晰的浏览线索。",
  },
];

export default function AboutPage() {
  usePageMeta(
    "关于78动漫 - 78动漫",
    "了解78动漫的平台定位、内容范围与联系方式。我们专注于为动漫爱好者提供最新动漫资讯、动画作品推荐及动漫解读内容。",
    "关于78动漫,动漫平台介绍,联系方式,版权声明,78动漫",
  );

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <PageHero
          icon="ri-information-line"
          title="关于78动漫"
          subtitle="78动漫是一个面向动漫爱好者的内容平台，致力于提供可靠及时的动漫资讯、优质作品推荐与深入的内容解读。"
          breadcrumb={[{ label: "关于78动漫" }]}
        />

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-primary-500" />
                平台定位
              </h2>
              <p className="mt-5 text-[15px] leading-loose text-foreground-700">
                78动漫专注于为动漫爱好者提供最新动漫资讯、动画作品推荐及动漫解读内容。我们的目标用户是热爱日本动漫及二次元文化的年轻群体，包括动画观众、漫画读者和动漫内容追踪者。
              </p>
              <p className="mt-4 text-[15px] leading-loose text-foreground-700">
                我们希望通过清晰的分类、便捷的搜索与个性化的收藏，帮助每一位用户更快地获取可靠信息、发现优质作品，并更深入地理解作品背后的内容与创作背景。
              </p>

              <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {scopes.map((scope) => (
                  <div
                    key={scope.title}
                    className="rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                      <i className={`${scope.icon} text-xl`} />
                    </span>
                    <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{scope.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-500">{scope.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="rounded-lg border border-background-200 bg-background-100 p-6 lg:sticky lg:top-24">
                <h2 className="flex items-center gap-2 font-heading text-lg font-bold text-foreground-950">
                  <i className="ri-contacts-line text-xl text-primary-600" />
                  联系方式
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-500">
                  如果你有任何建议、内容报错或合作意向，欢迎通过以下方式与我们联系。
                </p>
                <a
                  href="mailto:hello@78anime.demo"
                  className="mt-4 flex items-center gap-3 rounded-lg border border-background-300 bg-background-50 px-4 py-3 transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span className="flex flex-col">
                    <span className="text-xs text-foreground-400">邮箱（演示地址）</span>
                    <span className="text-sm font-medium text-foreground-900">hello@78anime.demo</span>
                  </span>
                </a>
                <div className="mt-4 flex flex-wrap gap-2">
                  {["微博", "哔哩哔哩", "微信公众号", "Discord"].map((item) => (
                    <span
                      key={item}
                      className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full border-y border-background-200 bg-background-100">
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-4 py-12 md:px-8 md:py-16 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
                <span className="h-6 w-1.5 rounded-full bg-accent-500" />
                意见反馈
              </h2>
              <p className="mt-5 text-[15px] leading-loose text-foreground-700">
                我们非常重视每一位用户的意见。填写下面的表单，把你对内容、功能或体验的想法告诉我们，我们会认真阅读每一条反馈。
              </p>
              <ul className="mt-6 flex flex-col gap-3">
                {["内容准确性建议", "功能体验反馈", "作品收录请求", "合作与内容共建"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5 text-sm text-foreground-600">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100 text-primary-600">
                      <i className="ri-check-line text-sm" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:col-span-7">
              <FeedbackForm />
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
          <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-foreground-950">
            <span className="h-6 w-1.5 rounded-full bg-secondary-500" />
            版权与免责声明
          </h2>
          <div className="mt-5 flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-6">
            <p className="text-sm leading-loose text-foreground-600">
              1. 本站为产品演示站点，站内所有动漫资讯、作品名称、评论文本、评分及配图均为虚构示范内容，不对应任何真实作品或机构，亦不构成任何推荐或承诺。
            </p>
            <p className="text-sm leading-loose text-foreground-600">
              2. 站内使用的人工智能生成图片仅用于界面展示，与任何真实影视、动漫或品牌无关，如涉及相似之处纯属巧合。
            </p>
            <p className="text-sm leading-loose text-foreground-600">
              3. 收藏、分享等功能均为前端演示：收藏数据仅保存在你的浏览器本地，分享按钮不会向任何社交平台实际发布内容，请勿据此产生误导性预期。
            </p>
            <p className="text-sm leading-loose text-foreground-600">
              4. 若你希望将本站用于正式运营，请自行替换为拥有合法授权的内容与素材，并遵守当地相关法律法规。
            </p>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/"
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
            <Link
              to="/news"
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600 cursor-pointer"
            >
              浏览动漫资讯
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}